import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import ProductFeatureSection from "../common/product-feature";
import '../styles/_features-section.module.scss';
import Image from "next/image";

type FeatureButton = {
  text: string;
  link: string;
};

type ProductFeature = {
    id: string;
    image: string;
    imageAlt: string;
    icon: string;
    heading: string;
    title: string;
    list: string[];
    buttonTxt?: FeatureButton[];
    exploreBtn?: string;
    exploreLink?: string;
    docsLink?: string;
    docsLabel?: string;
};

type Props = {
    productFeatures: ProductFeature[];
    tourId: string,
    className?: string,
    description?: string,
    isTryOut?: boolean,
    hasActionsDemo?: boolean,
}

const FeaturesMonitoringV2: React.FC<Props> = ({productFeatures, tourId, className, description, isTryOut, hasActionsDemo}) => {
    const { t } = useTranslation('common');
    const [activeTab, setActiveTab] = useState(null);
    const sections = useRef([]);
    const featuresSectionRef = useRef(null);
    
    // Ensure productFeatures is always an array
    const featuresArray = Array.isArray(productFeatures) ? productFeatures : []

    const handleScroll = () => {
        if (!featuresSectionRef.current) return;

        const scrollPosition = window.scrollY + window.innerHeight / 2;
        const sectionTop = featuresSectionRef.current.offsetTop;
        const sectionBottom = sectionTop + featuresSectionRef.current.offsetHeight;

        if (scrollPosition >= sectionTop && scrollPosition <= sectionBottom) {
            sections.current.forEach((section, index) => {
                if (section.offsetTop + sectionTop <= scrollPosition && section.offsetTop + sectionTop + section.offsetHeight > scrollPosition) {
                    setActiveTab(index);
                }
            });
        }
    };

    const handleClick = (index) => {
        setActiveTab(index);
        sections.current[index].scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <section ref={featuresSectionRef} className={"features-section product-page product-features-overview"}>
            <div className={'container'}>
                <p className={"desc-features"}>{description}</p>
                <div className={`tabs ${className}`}>
                    <ul className={"tabNav"}>
                        {featuresArray.map((item, index) => {
                            return (
                                <li
                                    className={`tab ${activeTab === index ? 'active' : ''}`}
                                    onClick={() => handleClick(index)}
                                    key={index}
                                >
                                    <p className="flex-icon-tabs"><Image src={item.icon} alt={item.heading} width={20} height={20} />{item.heading}</p>
                                </li>
                            );
                        })}
                    </ul>
                    <div className={'content'}>
                        {featuresArray.map((section, index) => (
                            <ProductFeatureSection
                                key={index}
                                id={section.id}
                                image={section.image}
                                imageAlt={section.imageAlt}
                                title={section.title}
                                list={section.list}
                                sectionRef={(el: any) => sections.current[index] = el}
                                hasActionsDemo={hasActionsDemo}
                                exploreBtn={section.exploreBtn}
                                exploreLink={section.exploreLink}
                                docsLink={section.docsLink}
                                docsLabel={section.docsLabel}
                            />
                        ))}
                    </div>
                </div>
</div>
        </section>
    );
};

export default FeaturesMonitoringV2;
